import { BREAKPOINT_ANALYSIS_SLUG, CAP_TABLE_SLUG, FUND_OWNERSHIP_SLUG } from 'common/constants/cap-table/capTable';
import { FINANCIAL_SLUG } from 'common/constants/financials/financials';
import { SUMMARY_SLUG } from 'common/constants/summary/summary';
import { VALUATIONS_SLUG } from 'common/constants/valuations/valuations';
import { WATERFALL_SLUG } from 'common/constants/waterfall';

export enum PdfExportFormat {
  DIGITAL = 'digital',
  A4 = 'a4',
  A3 = 'a3',
  LETTER = 'letter',
  LEGAL = 'legal',
}

export enum PdfExportOrientation {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

export const FORMAT_OPTIONS = [
  { value: PdfExportFormat.DIGITAL, label: 'Digital (Auto-fit)' },
  { value: PdfExportFormat.A4, label: 'A4' },
  { value: PdfExportFormat.A3, label: 'A3' },
  { value: PdfExportFormat.LETTER, label: 'Letter' },
  { value: PdfExportFormat.LEGAL, label: 'Legal' },
];

export const CONVERSION_RATIOS = {
  /** Conversion ratio from millimeters to pixels at 96 DPI */
  MM_TO_PX: 3.779527559,
} as const;

export const PAGE_DIMENSIONS = {
  /** Standard Letter page dimensions in millimeters */
  LETTER_WIDTH_MM: 216,
  LETTER_HEIGHT_MM: 279,
} as const;

export const RENDERING_DELAYS = {
  /** Default rendering delay in milliseconds */
  DEFAULT_DELAY_MS: 100,
  /** Extended delay for Digital format rendering in milliseconds */
  DIGITAL_DELAY_MS: 500,
} as const;

export const SCALE_LIMITS = {
  /** Minimum scale factor for Digital format */
  DIGITAL_MIN_SCALE: 2,
  /** Maximum scale factor for Digital format */
  DIGITAL_MAX_SCALE: 4,
  /** Base width for scale calculation in pixels */
  BASE_SCALE_WIDTH_PX: 800,
} as const;

export const ELEMENT_SPACING = {
  /** Spacing between elements in pixels */
  ELEMENT_MARGIN_TOP_PX: 20,
  /** Padding for temporary container in pixels */
  TEMP_CONTAINER_PADDING_PX: 10,
} as const;

export const TEMP_CONTAINER_POSITION = {
  /** Off-screen position for temporary containers */
  OFF_SCREEN_LEFT: '-9999px',
  /** Starting vertical position */
  TOP_POSITION: '0',
} as const;

export const IMAGE_SETTINGS = {
  MAX_QUALITY: 1,
  MEDIUM_QUALITY: 0.8,
  MAX_WIDTH_MM: 200,
  MAX_HEIGHT_MM: 150,
  PDF_ALIAS: undefined,
  PNG_FORMAT: 'png' as const,
  JPEG_FORMAT: 'jpeg' as const,
  COMPRESSION: 'MEDIUM' as const,
  PNG_MIME_TYPE: 'image/png' as const,
} as const;

export const FILE_SETTINGS = {
  /** Default filename for PDF export */
  DEFAULT_FILENAME: 'pdf-report',
  /** Default background color */
  DEFAULT_BG_COLOR: 'white',
  /** Default PDF unit */
  PDF_UNIT: 'mm' as const,
} as const;

export const PDF_SUPPORTED_PAGES = [
  SUMMARY_SLUG,
  FINANCIAL_SLUG,
  CAP_TABLE_SLUG,
  FUND_OWNERSHIP_SLUG,
  BREAKPOINT_ANALYSIS_SLUG,
  VALUATIONS_SLUG,
  WATERFALL_SLUG,
];

// Timeout constants for various operations
export const TIMEOUT_MS = {
  IFRAME_LOAD: 15000, // 15 seconds - timeout for iframe to load
  API_CHECK_RETRY: 500, // 500ms - retry interval for checking API calls
  DOWNLOAD_DELAY: 500, // 500ms - delay to ensure download has started
  DOWNLOAD_MAX_WAIT: 2000, // 2 seconds - maximum wait time for download to initiate
} as const;

// Progress percentage constants
export const PROGRESS_PERCENTAGE = {
  MAX: 100, // Maximum percentage (100%)
  CAP: 99, // Cap percentage to show until final completion
  CONVERSION_FACTOR: 100, // Factor to convert 0.0-1.0 to 0-100 percentage
} as const;

// Progress milestones for page processing (0.0 to 1.0 per page)
export const PROGRESS_MILESTONES = {
  INITIAL: 0.02,
  AFTER_IFRAME_LOAD: 0.15,
  BEFORE_API_WAIT: 0.2,
  AFTER_API_COMPLETE: 0.4,
  CAPTURING_HEADER: 0.5,
  CAPTURING_CONTENT: 0.7,
  CAPTURING_FOOTER: 0.85,
  PAGE_COMPLETE: 1.0,
} as const;

export const TEMPLATE_CONSTANTS = {
  COVER_VERTICAL_MARGIN_MM: 20,
  COVER_HORIZONTAL_MARGIN_MM: 10,
  PAGE_MARGIN_MM: 10,
  HEADER_TOTAL_HEIGHT_MM: 35,
  COVER_TEXT_SEPARATOR_MM: 2,
  COVER_TITLE_LINE_LENGTH_MM: 22,
  COVER_LOGO_WIDTH_MM: 35,
  COVER_LOGO_HEIGHT_MM: 10,
  COVER_LINE_LEFT_MARGIN_MM: 5,
  COVER_SECONDARY_LOGO_WIDTH_MM: 13.23, // 50px converted to mm
  COVER_SECONDARY_LOGO_HEIGHT_MM: 4.23, // 16px converted to mm
  COVER_SECONDARY_LOGO_TEXT_SPACING_MM: 3, // Space between text and logo
  COVER_SECONDARY_LINE_TEXT_SPACING_MM: 3, // Space between line and text,
  SCALAR_BLUE_LOGO_PNG: '/images/pdf/scalar-logo-blue.png',
  SCALAR_WHITE_LOGO_PNG: '/images/pdf/scalar-logo-white.png',
  COVER_BLUE_BACKGROUND_JPG: '/images/pdf/cover-blue.jpg',
  COVER_LIGHT_BACKGROUND_JPG: '/images/pdf/cover-light.jpg',
  PAGES_BACKGROUND_JPG: '/images/pdf/pages.jpg',
  INFO_ICON_PNG: '/images/pdf/info-icon.png',
} as const;

export const EXPORT_MODE_STYLES = {
  display: 'inline-block',
  width: 'auto',
} as const;